import axios from 'axios'
import Qs from 'qs'
import { Message, MessageBox, Loading } from 'element-ui'
import store from '@/store'
import { tansParams, Debounce } from '@/utils/index'
import { getToken } from '@/utils/auth'
import Vue from 'vue'
import defaultSettings from '@/settings'

let loadingInstance
// 当前正在请求的数量
let requestNum = 0
let activeRequestCount = 0
const goodsBenefitMessagePattern = /^缺少【(.+)】，请前往商城购买相关权益$/
const pendingGoodsBenefits = new Set()
let goodsBenefitDialogTimer
let goodsBenefitDialogVisible = false

axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8'
axios.defaults.isShowLoader = true

// 创建 axios 实例
const service = axios.create({
	baseURL: process.env.VUE_APP_BASE_API, // url = base url + request url
	timeout: 60000 // 请求超时
})

// request 请求拦截器
service.interceptors.request.use(
	config => {
		// 判断该接口是否需要设置token，默认需要token
		const isToken = (config.headers || {}).isToken === true
		if (getToken() && isToken) {
			config.headers['InternetToken'] = getToken()
		}

		// get 请求映射params参数
		if (config.method === 'get' && config.params) {
			let url = config.url + '?' + tansParams(config.params)
			url = url.slice(0, -1)
			config.params = {}
			config.url = url
		}

		// post 请求处理参数
		if (config.method === 'post') {
			const contentType = config.headers['Content-Type']

			if (contentType.indexOf('application/x-www-form-urlencoded') !== -1) {
				config.data = Qs.stringify(config.data)
			} else if (contentType.indexOf('application/json') !== -1) {
				config.data = JSON.stringify(config.data)
			}
		}

		if (config.isShowLoader) {
			showLoader()
		}
		activeRequestCount++

		return config
	},
	error => {
		// 请求错误时
		console.log(error) // for debug
		return Promise.reject(error)
	}
)

// response 响应拦截器
service.interceptors.response.use(
	/**
	 * If you want to get http information such as headers or status
	 * Please return  response => response
	 */

	/**
	 * Determine the request status by custom code
	 * Here is just an example
	 * You can also judge the status by HTTP Status Code
	 */
	response => {
		const res = response.data

		finishRequest()
		hideLoader()

		// 状态码不为200表示错误，状态以6开头的表示为会员权益使用前置校验状态码
		if (res.code !== 200 && !res.code.toString().startsWith('6')) {
			const goodsBenefitError = queueGoodsBenefitPurchaseDialog(res.message)
			if (!goodsBenefitError) {
				Message({
					message: res.message || 'Error',
					type: 'error',
					duration: 5 * 1000
				})
			}

			// 401: 无效token;
			if (res.code === 401 || res.code === 40205 || res.code === 40204) {
				// 重新登录
				store.dispatch('user/logout').then(() => {
					location.href = defaultSettings.publicPath + 'login'
				})
			}
			return Promise.reject(new Error(res.message || 'Error'))
		} else {
			return res
		}
	},
	error => {
		finishRequest()
		hideLoader()
		console.log('err' + error) // for debug
		Message({
			message: error.message,
			type: 'error',
			duration: 5 * 1000
		})
		return Promise.reject(error)
	}
)

function queueGoodsBenefitPurchaseDialog(message) {
	const match = goodsBenefitMessagePattern.exec(message || '')
	if (!match) return false

	pendingGoodsBenefits.add(match[1])
	scheduleGoodsBenefitPurchaseDialog()

	return true
}

function finishRequest() {
	activeRequestCount = Math.max(activeRequestCount - 1, 0)
}

function scheduleGoodsBenefitPurchaseDialog() {
	clearTimeout(goodsBenefitDialogTimer)
	goodsBenefitDialogTimer = setTimeout(showGoodsBenefitPurchaseDialog, 300)
}

function escapeHtml(value) {
	const htmlCharacters = {
		'&': '&amp;',
		'<': '&lt;',
		'>': '&gt;',
		'"': '&quot;',
		"'": '&#39;'
	}
	return value.replace(/[&<>"']/g, character => htmlCharacters[character])
}

function showGoodsBenefitPurchaseDialog() {
	if (goodsBenefitDialogVisible || pendingGoodsBenefits.size === 0) return

	// 等待当前页面的并发请求全部结束，再一次性展示所有缺失权益。
	if (activeRequestCount > 0) {
		scheduleGoodsBenefitPurchaseDialog()
		return
	}

	const benefitNames = Array.from(pendingGoodsBenefits)
	const benefitText = benefitNames
		.map(name => `<span style="color: #d93025; font-weight: 600;">【${escapeHtml(name)}】</span>`)
		.join('、')
	pendingGoodsBenefits.clear()
	goodsBenefitDialogVisible = true

	MessageBox.confirm(`缺少${benefitText}，请前往商城购买相关权益`, '商品权益不足', {
		confirmButtonText: '商城购买',
		cancelButtonText: '暂不购买',
		type: 'warning',
		dangerouslyUseHTMLString: true,
		closeOnClickModal: false
	})
		.then(() => {
			location.href = defaultSettings.publicPath + 'mallStore/index'
		})
		.catch(() => {})
		.then(() => {
			goodsBenefitDialogVisible = false
			scheduleGoodsBenefitPurchaseDialog()
		})
}

function showDownloadError(message) {
	if (!queueGoodsBenefitPurchaseDialog(message)) {
		Message.error(message || '下载失败')
	}
}

function readBlobAsJson(blob) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => {
			try {
				resolve(JSON.parse(reader.result))
			} catch (error) {
				reject(error)
			}
		}
		reader.onerror = reject
		reader.readAsText(blob)
	})
}

export function showLoader() {
	// 每次请求增加一次，当requestNum等于1时，弹出loading，防止重复弹出
	if (requestNum === 0 && !loadingInstance) {
		loadingInstance = Loading.service({
			lock: true,
			text: '加载中，请稍后...',
			spinner: 'el-icon-loading',
			background: 'rgba(0, 0, 0, 0.7)'
		})
	}
	requestNum++
}

export function hideLoader() {
	// 如果请求数量等于0，则取消loading
	Vue.nextTick(() => {
		// 以服务的方式调用的 Loading 需要异步关闭
		requestNum--
		// 保障requestNum永远大于等于0
		requestNum = Math.max(requestNum, 0)
		if (requestNum === 0) {
			if (axios.defaults.isShowLoader && loadingInstance) {
				closeLoading()
			}
		}
	})
}

/**
 * 防抖：将 300ms 间隔内的关闭 loading 便合并为一次。防止连续请求时， loading闪烁的问题。
 * 因为有时会碰到在一次请求完毕后又立刻又发起一个新的请求的情况（比如删除一个表格一行后立刻进行刷新）
 * 这种情况会造成连续 loading 两次，并且中间有一次极短的闪烁。通过防抖可以让 300ms 间隔内的 loading 便合并为一次，避免闪烁的情况。
 * @type {(function(): void)|*}
 */
const closeLoading = Debounce(() => {
	loadingInstance.close()
	loadingInstance = null
}, 300)

/**
 * 报告生成并下载接口
 * @param {*} url
 * @param {*} data
 * @param {*} options
 * @returns
 */
export function reportGenerationDownload(url, data, options = {}) {
	// 创建一个新的axios实例，避开全局拦截器
	const downloadInstance = axios.create({
		baseURL: process.env.VUE_APP_BASE_API,
		timeout: options.timeout || 120000, // 下载文件可能需要更长的超时时间
		responseType: 'blob', // 设置响应类型为blob
		headers: {
			'Content-Type': 'multipart/form-data',
			InternetToken: getToken()
		}
	})

	// 显示加载中
	const loading = Loading.service({
		lock: true,
		text: options.loadingText || '报告生成中，请稍候...',
		spinner: 'el-icon-loading',
		background: 'rgba(0, 0, 0, 0.7)'
	})

	return downloadInstance
		.post(url, data)
		.then(async response => {
			const contentType = response.headers['content-type'] || ''
			if (contentType.indexOf('application/json') !== -1) {
				const errorData = await readBlobAsJson(response.data)
				const error = new Error(errorData.message || '下载失败')
				error.isHandled = true
				showDownloadError(error.message)
				throw error
			}

			loading.close()

			console.log('response:', response)

			let filename = options.defaultFileName || '下载文件.pdf'

			// 获取文件名（这里会从响应头中'content-disposition'取值，由于后端没有返回，这里屏蔽词逻辑）
			// const disposition = response.headers['content-disposition']
			// if (disposition && disposition.indexOf('filename=') !== -1) {
			// 	const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
			// 	const matches = filenameRegex.exec(disposition)
			// 	if (matches != null && matches[1]) {
			// 		filename = matches[1].replace(/['"]/g, '')
			// 		// 解码文件名（如果是中文）
			// 		try {
			// 			filename = decodeURIComponent(filename)
			// 		} catch (e) {
			// 			console.error('解码文件名失败', e)
			// 		}
			// 	}
			// }

			// 创建Blob对象
			const blob = new Blob([response.data], {
				type: response.headers['content-type'] || 'application/octet-stream'
			})

			// 创建下载链接并触发下载
			const link = document.createElement('a')
			link.href = URL.createObjectURL(blob)
			link.download = filename
			document.body.appendChild(link)
			link.click()

			// 释放URL对象和移除链接
			setTimeout(() => {
				URL.revokeObjectURL(link.href)
				document.body.removeChild(link)
			}, 100)

			return {
				success: true,
				message: '下载成功'
			}
		})
		.catch(async error => {
			loading.close()
			if (error.isHandled) throw error

			// 尝试解析错误响应
			if (error.response && error.response.data) {
				try {
					const errorData = await readBlobAsJson(error.response.data)
					showDownloadError(errorData.message)
				} catch (e) {
					Message.error('下载失败')
				}
			} else {
				Message.error(error.message || '网络错误，下载失败')
			}

			return Promise.reject(error)
		})
}

export default service
